/**
 * sortBooks - Calil 検索結果を指定キー・順序でソートするユーティリティ
 *
 * - relevance: そのままの順序を維持（並び替えなし）
 * - pubdate  : 発行日文字列から数値(YYYYMM 相当)を抽出して比較。未設定は末尾へ
 * - title    : Intl.Collator('ja') で自然順に比較
 * - author   : Intl.Collator('ja') で自然順に比較。未設定は末尾へ
 * - holdings : holdings ∪ estimated_holdings ∪ url の重複除去後の数で比較。未設定は 0
 */

import type { CalilBook } from '@/api/types/calil'
import type { LibrarySortKey, LibrarySortOrder } from '@/store/useLibrarySortStore'

import { collectHoldingIds } from './holdings'

const collator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' })

/** pubdate 文字列/数値から比較用の数値 (YYYYMM) を抽出。未取得時は null */
const extractPubdateValue = (pubdate: string | number | null | undefined): number | null => {
  if (pubdate == null) return null
  const str = String(pubdate)
  const yearMatch = /(\d{4})/.exec(str)
  if (!yearMatch) return null
  const year = Number(yearMatch[1])
  const monthMatch = /\d{4}\D+(\d{1,2})/.exec(str)
  const month = monthMatch ? Math.min(12, Math.max(1, Number(monthMatch[1]))) : 1
  return year * 100 + month
}

/** 所蔵館数 = holdings ∪ estimated_holdings ∪ url の重複除去後の ID 数 */
const holdingsCount = (book: CalilBook): number => collectHoldingIds(book).length

/**
 * 値比較の結果を「null 判定 (nullCmp)」と「値比較 (valueCmp)」に分けて返す。
 *  - nullCmp !== 0 のとき: どちらかが null。呼び出し側は factor を適用せず末尾に寄せる。
 *  - nullCmp === 0 のとき: 両方 valid。呼び出し側で valueCmp * factor を適用する。
 * こうすることで、`sortBooks` の `factor` (-1) を掛けても null 位置が反転しない。
 */
type SplitCmp = { nullCmp: number; valueCmp: number }

const splitWithNullLast = <T extends NonNullable<unknown>>(
  a: T | null | undefined,
  b: T | null | undefined,
  cmp: (x: T, y: T) => number,
): SplitCmp => {
  if (a == null && b == null) return { nullCmp: 0, valueCmp: 0 }
  if (a == null) return { nullCmp: 1, valueCmp: 0 }
  if (b == null) return { nullCmp: -1, valueCmp: 0 }
  return { nullCmp: 0, valueCmp: cmp(a, b) }
}

const splitByKey = (a: CalilBook, b: CalilBook, key: LibrarySortKey): SplitCmp => {
  if (key === 'pubdate') {
    return splitWithNullLast(extractPubdateValue(a.pubdate), extractPubdateValue(b.pubdate), (x, y) => x - y)
  }
  if (key === 'title') {
    return splitWithNullLast(a.title || null, b.title || null, (x, y) => collator.compare(x, y))
  }
  if (key === 'author') {
    return splitWithNullLast(a.author ?? null, b.author ?? null, (x, y) => collator.compare(x, y))
  }
  if (key === 'holdings') {
    return { nullCmp: 0, valueCmp: holdingsCount(a) - holdingsCount(b) }
  }
  return { nullCmp: 0, valueCmp: 0 }
}

export const sortBooks = (books: CalilBook[], key: LibrarySortKey, order: LibrarySortOrder): CalilBook[] => {
  if (key === 'relevance') return [...books]
  const factor = order === 'desc' ? -1 : 1
  return [...books].sort((a, b) => {
    const { nullCmp, valueCmp } = splitByKey(a, b, key)
    // null は order に依らず常に末尾へ (factor を適用しない)
    if (nullCmp !== 0) return nullCmp
    return valueCmp * factor
  })
}
